/**
 * TRANG PHỤC — "DÁNG" (skill form) của từng class, ĐỌC TỪ MANIFEST DO TOOL SINH RA.
 *
 * Người dùng: *"tạo sẵn cho tôi các thư mục cho các Class, khi tôi cho ảnh 3x3 vào thư mục,
 * skill sẽ được thêm vào class tương ứng… tên skill chính là tên file 3x3 tôi để vào"*.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * ĐỔI CƠ CHẾ (v7): `import.meta.glob` → `manifest.json`
 * ────────────────────────────────────────────────────────────────────────────
 * TRƯỚC: Vite quét `art/skills/<class>/*.png` lúc build rồi NHÚNG ảnh vào bundle
 * (`dist/assets/<tên>-<hash>.png`). Hệ quả đo được: `npm run build` ra **8 file PNG
 * 4–5 MB = 37 MB** nằm trong bundle, dù web chỉ cần ô 232px. Thêm nữa, ảnh dáng đi
 * đường KHÁC hẳn mọi ảnh còn lại của game (`art/ai/` đi qua `public/`).
 *
 * NAY: `tools/sync-art.mjs` (chạy ở `predev`/`prebuild`) đọc `art/skills/` và sinh:
 *   · `public/art/skills/<class>/<tên>.webp`  — ảnh ĐÃ NÉN (giảm ~94%)
 *   · `public/art/skills/manifest.json`       — danh mục: có dáng nào, tên gì
 * Trang nạp manifest này lúc boot rồi `fetch` ảnh theo đường dẫn TĨNH — giống hệt
 * cách `art/ai/` đang chạy. Nhờ vậy `dist/` không còn nhúng PNG dáng nào.
 *
 * ⚠️ VÌ SAO PHẢI CÓ MANIFEST (không hardcode được như `AI_SHEETS`):
 * `art/ai/` có đúng 5 class cố định nên hardcode được. `art/skills/` thì người dùng
 * THẢ ẢNH BẤT KỲ LÚC NÀO ⇒ số dáng không biết trước. Mà trình duyệt KHÔNG đọc được
 * danh sách file trong thư mục lúc chạy ⇒ phải có ai đó CHỐT danh sách trước.
 *
 * ⚠️ ĐỔI LẠI, MẤT TÍNH "CHỈ F5": thả ảnh mới phải chạy `npm run sync:art` (hoặc
 * `npm run dev`/`npm run build` — 2 lệnh này tự chạy qua `predev`/`prebuild`).
 *
 * ⚠️ `SKILL_FORMS` NAY LÀ MẢNG RỖNG LÚC ĐẦU, ĐẦY DẦN SAU KHI `loadWardrobe()` XONG.
 * Mọi nơi đọc danh mục PHẢI chờ `loadWardrobe()` (gọi trong màn Loading, xem
 * `src/game.ts`). Đây là điểm dễ sinh lỗi nhất của lần đổi này: đọc sớm ⇒ danh sách
 * TRỐNG và tab Trang phục hiện "0 dáng" dù ảnh có thật.
 *
 * Tên file = TÊN SKILL (bỏ đuôi + bỏ tiền tố số xếp thứ tự). Thư mục lạ thì bỏ qua và cảnh báo,
 * KHÔNG làm chết game.
 */
import { CLASS_ORDER, type ClassId } from '../data/classes';

export interface SkillFormDef {
  /** class sở hữu dáng này */
  classId: ClassId;
  /** khoá ổn định để lưu vào save (slug không dấu của tên hiển thị) */
  id: string;
  /** tên hiển thị = tên file bỏ đuôi (đã bỏ tiền tố số) */
  name: string;
  /** tên file gốc — để báo cho người dùng biết sửa file nào */
  file: string;
  /** khoá đường dẫn đầy đủ trong repo */
  key: string;
  /** URL THẬT để nạp ảnh (`.webp` sau khi nén; rỗng nếu manifest thiếu) */
  src: string;
}

/** Đường dẫn manifest do `tools/sync-art.mjs` sinh ra trong `public/`. */
const MANIFEST_URL = 'art/skills/manifest.json';

/** Slug không dấu, an toàn cho save + khoá atlas: "Thiết Quyền" → "thiet-quyen". */
export function slugify(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/gi, 'd')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Tên hiển thị từ tên file: bỏ đuôi, bỏ TIỀN TỐ SỐ dùng để xếp thứ tự.
 * Vì sao bỏ tiền tố: người dùng chốt "tên skill chính là tên file" — nên `1-Thiết Quyền.png`
 * phải hiện là "Thiết Quyền" (số chỉ để xếp thứ tự), không hiện "1-Thiết Quyền".
 */
export function displayNameOf(file: string): string {
  const noExt = file.replace(/\.[a-z0-9]+$/i, '');
  return noExt.replace(/^\s*\d+\s*[-._)]\s*/, '').trim() || noExt.trim();
}

/** Số xếp thứ tự ở đầu tên file (không có ⇒ +∞, tức xếp sau các file có đánh số). */
export function orderOf(name: string): number {
  const m = /^(\d+)\s*[-._)\s]/.exec(name);
  return m ? Number(m[1]) : Number.POSITIVE_INFINITY;
}

/** Tách 1 khoá glob thành dáng; trả null nếu đường dẫn không đúng dạng. */
export function parseSkillFormKey(key: string): { classId: ClassId; file: string } | null {
  const parts = key.split('/').filter(Boolean);
  // ['art', 'skills', '<class>', '<file>']
  if (parts.length !== 4 || parts[0] !== 'art' || parts[1] !== 'skills') return null;
  const folder = parts[2].toLowerCase();
  const classId = CLASS_ORDER.find((c) => c === folder);
  if (!classId) return null;
  return { classId, file: parts[3] };
}

/** Khoá dựng từ class + tên file — dùng CHUNG cho `buildForms` và manifest. */
function keyOf(classId: string, file: string): string {
  return `art/skills/${classId}/${file}`;
}

/**
 * DỰNG DÁNG TỪ DANH SÁCH KHOÁ — HÀM THUẦN, giữ nguyên từ bản dùng glob để test cũ
 * (`tests/wardrobe.test.ts`) vẫn chạy được và vẫn là "nguồn sự thật" của quy tắc đặt tên.
 */
export function buildForms(keys: string[]): { forms: SkillFormDef[]; warnings: string[] } {
  const forms: SkillFormDef[] = [];
  const warnings: string[] = [];
  const seen = new Map<string, string>();
  for (const key of keys) {
    const parsed = parseSkillFormKey(key);
    if (!parsed) {
      warnings.push(`bỏ qua ảnh không thuộc class nào: ${key} (chỉ nhận art/skills/<${CLASS_ORDER.join('|')}>/<ten>.png)`);
      continue;
    }
    const name = displayNameOf(parsed.file);
    const id = slugify(name) || slugify(parsed.file);
    if (!id) {
      warnings.push(`bỏ qua file không có tên hợp lệ: ${key}`);
      continue;
    }
    const dupKey = `${parsed.classId}:${id}`;
    const prev = seen.get(dupKey);
    if (prev) {
      warnings.push(`trùng tên "${name}" trong ${parsed.classId}/ (${prev} và ${parsed.file}) — chỉ nhận file xếp trước`);
      continue;
    }
    seen.set(dupKey, parsed.file);
    // `src` để rỗng — `applyManifest` (chạy sau) mới biết URL THẬT (`.webp` sau khi nén).
    forms.push({ classId: parsed.classId, id, name, file: parsed.file, key, src: '' });
  }
  forms.sort((a, b) => {
    const ca = CLASS_ORDER.indexOf(a.classId) - CLASS_ORDER.indexOf(b.classId);
    if (ca !== 0) return ca;
    const oa = orderOf(a.file);
    const ob = orderOf(b.file);
    if (oa !== ob) return oa - ob;
    return a.name.localeCompare(b.name, 'vi');
  });
  return { forms, warnings };
}

/*
 * ── TRẠNG THÁI DANH MỤC (nạp bất đồng bộ) ──────────────────────────────────
 * Ban đầu RỖNG; `loadWardrobe()` điền vào. UI đọc qua `formsForClass`/`skillFormById`
 * nên chỉ cần `loadWardrobe()` xong TRƯỚC lần vẽ đầu tiên là an toàn.
 */
let catalog: { forms: readonly SkillFormDef[]; warnings: readonly string[] } = { forms: [], warnings: [] };

/**
 * NẠP DANH MỤC DÁNG từ `manifest.json` (do `tools/sync-art.mjs` sinh).
 *
 * Gọi trong màn Loading (xem `src/game.ts`) — TRƯỚC khi HUD vẽ lần đầu.
 *
 * ⚠️ KHÔNG BAO GIỜ NÉM LỖI. Thiếu manifest (chưa chạy `sync:art`) chỉ làm danh mục
 * RỖNG: tab Trang phục hiện "0 dáng" và nhân vật dùng dáng gốc — game vẫn chơi được.
 * Đó là hành vi cố ý: 1 file thiếu KHÔNG được làm chết cả game.
 *
 * ⚠️ Nạp LẠI được (gọi nhiều lần) — cần cho công cụ kiểm chứng đổi ảnh giữa 2 lần đo.
 */
export async function loadWardrobe(): Promise<void> {
  try {
    const res = await fetch(MANIFEST_URL, { cache: 'no-cache' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const raw: unknown = await res.json();
    if (!Array.isArray(raw)) throw new Error('manifest không phải mảng');

    /*
     * Dựng theo CÙNG hàm thuần `buildForms` để quy tắc tên/ thứ tự/ chống trùng KHÔNG
     * bị lệch giữa đường manifest và đường test.
     */
    const keys: string[] = [];
    /** URL thật theo khoá — manifest là nguồn duy nhất biết `.webp` hay `.png`. */
    const srcByKey = new Map<string, string>();
    for (const item of raw) {
      const it = item as { classId?: unknown; file?: unknown; src?: unknown };
      if (typeof it.classId !== 'string' || typeof it.file !== 'string') continue;
      const k = keyOf(it.classId, it.file);
      keys.push(k);
      if (typeof it.src === 'string' && it.src) srcByKey.set(k, it.src);
    }

    const built = buildForms(keys);
    /** Gắn URL nạp ảnh vào từng dáng (manifest thiếu `src` ⇒ bỏ dáng đó, tránh 404). */
    const withSrc = built.forms
      .map((f) => ({ ...f, src: srcByKey.get(f.key) ?? '' }))
      .filter((f) => f.src !== '');
    catalog = { forms: withSrc, warnings: built.warnings };
    for (const w of built.warnings) console.warn('[trang phục]', w);
  } catch (err) {
    /*
     * Manifest thiếu/ hỏng. KHÔNG xoá danh mục đang có (nếu `loadWardrobe` được gọi
     * lần 2 và mạng lỗi) — giữ bản cũ còn hơn làm tab Trang phục trắng.
     */
    console.warn(
      `[trang phục] không đọc được ${MANIFEST_URL} — danh mục dáng để trống. ` +
        `Chạy \`npm run sync:art\` để sinh lại. Lỗi:`,
      err,
    );
  }
}

/** Toàn bộ dáng tìm được (RỖNG nếu chưa `loadWardrobe()` hoặc chưa có ảnh nào). */
export function SKILL_FORMS(): readonly SkillFormDef[] {
  return catalog.forms;
}
/** Cảnh báo lúc dựng danh mục (thư mục lạ, tên trùng) — UI có thể hiển thị. */
export function SKILL_FORM_WARNINGS(): readonly string[] {
  return catalog.warnings;
}

/** Dáng của 1 class, theo thứ tự đã sắp. */
export function formsForClass(classId: ClassId): SkillFormDef[] {
  return catalog.forms.filter((f) => f.classId === classId);
}

/** Tra 1 dáng theo class + id (id sai ⇒ undefined, KHÔNG ném lỗi — save cũ có thể chứa id lạ). */
export function skillFormById(classId: ClassId, id: string | undefined): SkillFormDef | undefined {
  if (!id) return undefined;
  return catalog.forms.find((f) => f.classId === classId && f.id === id);
}

/**
 * URL ảnh của dáng — nay lấy THẲNG từ manifest (`src`), không còn nạp qua bundler.
 * Giữ hàm này để nơi gọi (`unitview.ts`) không phải đổi, và để trả `null` khi thiếu.
 */
export function skillFormUrl(def: SkillFormDef): string | null {
  return def.src || null;
}

/**
 * Khoá của mọi dáng đang có (để test/ công cụ đối chiếu).
 * Trước đây là khoá glob; nay là khoá dựng từ manifest — cùng định dạng.
 */
export function skillFormKeys(): string[] {
  return catalog.forms.map((f) => f.key);
}

/** Số ảnh tìm được theo từng class — dùng cho UI + công cụ kiểm tra. */
export function skillFormCounts(): Record<ClassId, number> {
  const out = {} as Record<ClassId, number>;
  for (const c of CLASS_ORDER) out[c] = 0;
  for (const f of catalog.forms) out[f.classId]++;
  return out;
}
